import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { RouterLink } from '@angular/router';
import { debounceTime, distinctUntilChanged, map, skip, Subscription } from 'rxjs';
import { LoadingInlineComponent } from '@/app/atoms/loading-inline/loading-inline.component';
import { MiscService } from '@/app/services/misc/misc.service';
import { formatBriefTrackerDate } from '../../brief-tracker/brief-tracker.helpers';
import { AdminBusinessStatsService } from '../../../services/admin-business-stats.service';
import {
  BusinessStatsRowDto,
  BusinessStatsSortField,
  BusinessStatsSortOrder,
} from '../business-stats.types';
import { withShoutAgencyBrandFilter } from '../business-stats.helpers';

type BusinessStatsCopyField = 'businessAccountId' | 'agencyId';

@Component({
  selector: 'admin-tool-business-stats-list',
  templateUrl: './business-stats-list.html',
  styleUrl: './business-stats-list.scss',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [InfiniteScrollDirective, LoadingInlineComponent, RouterLink],
})
export class BusinessStatsList implements OnDestroy {
  rows: BusinessStatsRowDto[] = [];
  loadingState: 'idle' | 'loading' | 'loaded' | 'error' = 'idle';
  loadingMore = false;
  error: string | null = null;
  sortField: BusinessStatsSortField = 'searches';
  sortOrder: BusinessStatsSortOrder = 'DESC';
  hasMore = true;
  totalCount = 0;
  searchQuery = signal('');
  showShoutAgencyBrands = signal(true);
  copiedItemKey = signal<string | null>(null);

  readonly scrollDistance = 2;
  readonly scrollUpDistance = 1.5;
  readonly throttle = 300;
  readonly formatDate = formatBriefTrackerDate;

  private readonly pageSize = 25;
  private skip = 0;
  private fetchSubscriptionList = new Subscription();
  private readonly searchSubscription = new Subscription();

  private copiedResetTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly adminBusinessStatsService = inject(AdminBusinessStatsService);
  private readonly miscService = inject(MiscService);

  constructor() {
    this.searchSubscription.add(
      toObservable(this.searchQuery)
        .pipe(
          map((query) => query.trim()),
          debounceTime(300),
          distinctUntilChanged(),
          skip(1),
        )
        .subscribe(() => this.loadRows(true)),
    );

    this.loadRows(true);
  }

  ngOnDestroy(): void {
    if (this.copiedResetTimeoutId) {
      clearTimeout(this.copiedResetTimeoutId);
    }
    this.fetchSubscriptionList.unsubscribe();
    this.searchSubscription.unsubscribe();
  }

  rowKey(row: BusinessStatsRowDto): string {
    return row.businessAccountId;
  }

  getCopyKey(row: BusinessStatsRowDto, field: BusinessStatsCopyField): string {
    return `${this.rowKey(row)}:${field}`;
  }

  isCopied(copyKey: string): boolean {
    return this.copiedItemKey() === copyKey;
  }

  copyId(
    event: Event,
    value: string | null | undefined,
    label: string,
    copyKey: string,
  ): void {
    event.stopPropagation();
    if (!value) {
      return;
    }

    this.miscService.copyToClipboard(value, label);
    this.copiedItemKey.set(copyKey);

    if (this.copiedResetTimeoutId) {
      clearTimeout(this.copiedResetTimeoutId);
    }

    this.copiedResetTimeoutId = setTimeout(() => {
      this.copiedItemKey.set(null);
      this.copiedResetTimeoutId = null;
      this.cdr.markForCheck();
    }, 2000);

    this.cdr.markForCheck();
  }

  toggleSort(field: BusinessStatsSortField): void {
    if (this.sortField !== field) {
      this.sortField = field;
      this.sortOrder = 'DESC';
    } else {
      this.sortOrder = this.sortOrder === 'ASC' ? 'DESC' : 'ASC';
    }
    this.loadRows(true);
  }

  isSortedBy(field: BusinessStatsSortField): boolean {
    return this.sortField === field;
  }

  get isLoading(): boolean {
    return this.loadingState === 'loading';
  }

  get hasRowData(): boolean {
    return this.rows.length > 0;
  }

  get recordCountLabel(): string {
    if (this.isLoading && this.rows.length === 0) {
      return '—';
    }
    return `${this.rows.length}/${this.totalCount}`;
  }

  onScrollDown(): void {
    if (this.isLoading || this.loadingMore || !this.hasMore) {
      return;
    }
    this.loadRows(false);
  }

  onSearchInput(event: Event): void {
    this.searchQuery.set((event.target as HTMLInputElement).value);
    this.cdr.markForCheck();
  }

  onShowShoutAgencyBrandsChange(show: boolean): void {
    this.showShoutAgencyBrands.set(show);
    this.loadRows(true);
  }

  private loadRows(reset: boolean): void {
    if (reset) {
      this.fetchSubscriptionList.unsubscribe();
      this.fetchSubscriptionList = new Subscription();
      this.skip = 0;
      this.hasMore = true;
      this.totalCount = 0;
      this.rows = [];
      this.error = null;
      this.loadingState = 'loading';
      this.loadingMore = false;
    } else if (!this.hasMore || this.loadingMore || this.isLoading) {
      return;
    } else {
      this.loadingMore = true;
    }

    const search = this.searchQuery().trim();
    const body = withShoutAgencyBrandFilter(
      {
        take: this.pageSize,
        skip: this.skip,
        sortField: this.sortField,
        sortOrder: this.sortOrder,
        ...(search ? { search } : {}),
      },
      this.showShoutAgencyBrands(),
    );

    const sub = this.adminBusinessStatsService.list(body).subscribe({
      next: (result) => {
        const received = Array.isArray(result?.data) ? result.data : [];
        this.totalCount = result?.total ?? this.totalCount;
        this.rows = reset ? received : [...this.rows, ...received];
        this.skip += received.length;
        this.hasMore = this.rows.length < this.totalCount;
        this.loadingState = 'loaded';
        this.loadingMore = false;
        this.cdr.markForCheck();
      },
      error: (err: { error?: { message?: string }; message?: string }) => {
        this.error = err?.error?.message || err?.message || 'Failed to load business stats.';
        this.loadingState = reset ? 'error' : 'loaded';
        this.loadingMore = false;
        if (!reset) {
          this.hasMore = false;
        }
        this.cdr.markForCheck();
      },
    });

    this.fetchSubscriptionList.add(sub);
  }
}
